import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import type { Patient } from '../types/patient';
import { PatientApi } from '../utils/ApiService';

export interface PatientContextType {
  patients: Patient[];
  loading: boolean;
  error: string | null;
  getPatientById: (id: string) => Promise<Patient | null>;
  addPatient: (patient: Omit<Patient, 'id'>) => Promise<string | null>;
  updatePatient: (id: string, patient: Partial<Patient>) => Promise<boolean>;
  deletePatient: (id: string) => Promise<boolean>;
  refreshPatients: () => Promise<void>;
}

const PatientContext = createContext<PatientContextType | undefined>(undefined);

export const PatientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await PatientApi.getAll();

      if (response.success) {
        // Always ensure we have a proper array even if the API returns null/undefined
        const patientData = response.data || [];
        setPatients(patientData);
      } else {
        console.error('Failed to load patients:', response.error);
        setError(response.error || 'Failed to load patients');
        // Set empty array to prevent rendering errors
        setPatients([]);
      }
    } catch (err) {
      console.error('Unexpected error loading patients:', err);
      setError('An unexpected error occurred');
      // Set empty array to prevent rendering errors
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, []);

  const getPatientById = async (id: string): Promise<Patient | null> => {
    try {
      const response = await PatientApi.getById(id);

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (err) {
      console.error('Error getting patient by ID:', err);
      return null;
    }
  };

  const addPatient = async (patient: Omit<Patient, 'id'>): Promise<string | null> => {
    try {
      const response = await PatientApi.create(patient);

      if (response.success && response.data?.id) {
        await loadPatients(); // Refresh the patient list
        return response.data.id;
      }

      return null;
    } catch (err) {
      console.error('Error adding patient:', err);
      return null;
    }
  };

  const updatePatient = async (id: string, patient: Partial<Patient>): Promise<boolean> => {
    try {
      const response = await PatientApi.update(id, patient);

      if (response.success) {
        await loadPatients(); // Refresh the patient list
        return true;
      }

      return false;
    } catch (err) {
      console.error('Error updating patient:', err);
      return false;
    }
  };

  const deletePatient = async (id: string): Promise<boolean> => {
    try {
      const response = await PatientApi.delete(id);

      if (response.success) {
        await loadPatients(); // Refresh the patient list
        return true;
      }

      return false;
    } catch (err) {
      console.error('Error deleting patient:', err);
      return false;
    }
  };

  const refreshPatients = async (): Promise<void> => {
    await loadPatients();
  };

  const value = {
    patients,
    loading,
    error,
    getPatientById,
    addPatient,
    updatePatient,
    deletePatient,
    refreshPatients,
  };

  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>;
};

export const usePatients = (): PatientContextType => {
  const context = useContext(PatientContext);

  if (context === undefined) {
    throw new Error('usePatients must be used within a PatientProvider');
  }

  return context;
};
